import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";

// Components
import Sidebar from "../components/sidebar/Sidebar";
import Navbar from "../components/nav/Navbar";
import AppRoutes from "../routes/AppRoutes";
import { useLocation } from "react-router-dom";
import { GET_USER_DETAILS } from "../apis/dashboard-apis/dashboardDetailApi";
import { GET_FRAMEWORK_LIST } from "../apis/settingsApis/framework-apis/frameworkApis";
import UnauthorizePage from "../components/ErrorPages/UnauthorizePage";
import Role from "../utils/roles";
import { useAuth0 } from "@auth0/auth0-react";
import useAppStore from "../stores";
import { useRestrictedModuleAccess } from "../components/AccessControl";

const MainContent = styled("main")(({ theme, drawerOpen }) => ({
  flexGrow: 1,
  transition: "width 0.3s",
  width: `calc(100% - ${drawerOpen ? 240 : 62}px)`,
  overflow: "hidden",
}));

function Mainpage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { data, loading, error } = useQuery(GET_USER_DETAILS);
  const {
    data: frameworkData,
    loading: frameworkLoading,
    error: frameworkError,
  } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "cache-and-network",
  });
  const [activeTab, setActiveTab] = useState(location.pathname.split("/")[1]);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [isEmployeeMode, setIsEmployeeMode] = useState(false);
  const [isAuditorMode, setIsAuditorMode] = useState(false);
  const [breadcrumbLists, setBreadcrumbLists] = useState([]);
  const [showSecurityDetails, setShowSecurityDetails] = useState(false);
  const [securityDetailsData, setSecurityDetailsData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [initialRedirectDone, setInitialRedirectDone] = useState(false);
  const isRestrictedAccess = useRestrictedModuleAccess();

  // Framework store actions
  const setFrameworksData = useAppStore((state) => state.setFrameworksData);

  // Initialize frameworks when data is loaded
  useEffect(() => {
    if (frameworkData?.getFramework) {
      setFrameworksData(frameworkData.getFramework);
    }
  }, [frameworkData, setFrameworksData]);

  const handleLogOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  useEffect(() => {
    if (!loading && !frameworkLoading && !initialRedirectDone) {
      // Check for framework loading errors first
      if (frameworkError) {
        setErrorMessage(
          "Failed to load application frameworks. Please refresh the page or contact support."
        );
        return;
      }
      if (error || !data?.getAccountDetails?.userDetails) {
        // Handle error or null data
        console.log("data");
        const apiErrorMessage =
          error?.message ||
          data?.errors?.[0]?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(apiErrorMessage);
        handleLogOut();
        return;
      }

      const currentPath = location.pathname;

      const userDetails = data.getAccountDetails.userDetails || {};
      const isAdmin = userDetails?.roles?.some(
        (role) => role?.role_description == Role.ADMIN
      );
      const isAuditor = userDetails?.roles?.some(
        (role) => role?.role_description == Role.AUDITOR
      );

      // Restricted tenants only get the admin view; AppRoutes redirects any
      // path outside the allowed modules
      if (isRestrictedAccess) {
        setIsEmployeeMode(false);
        setIsAuditorMode(false);
        setInitialRedirectDone(true);
        return;
      }

      // Redirect auditors away from admin/employee routes
      if (isAuditor && (currentPath.startsWith("/admin/") || currentPath.startsWith("/employee/"))) {
        navigate("/auditor/audits", { replace: true });
        setInitialRedirectDone(true);
        return;
      }

      // Hardcoded employee route detection
      // TODO: Refactor this to use the routes.js file, quickfix
      const isEmployeeRoute =
        currentPath.startsWith("/employee/") ||
        currentPath.startsWith("/policy/ack/") ||
        currentPath.startsWith("/training/");
      const isAuditorRoute = currentPath.startsWith("/auditor/");

      if (isEmployeeRoute) {
        setIsEmployeeMode(true);
        setIsAuditorMode(false);
      } else if (isAuditorRoute) {
        setIsEmployeeMode(false);
        setIsAuditorMode(true);
      } else if (currentPath.startsWith("/admin/")) {
        setIsEmployeeMode(false);
        setIsAuditorMode(isAuditor);  // Keep auditor mode if user is auditor
      } else {
        // Fallback: non-prefixed / unknown routes use role to decide
        setIsEmployeeMode(!isAdmin && !isAuditor);
        setIsAuditorMode(isAuditor);
      }

      // Only auto-redirect from root, not when already on a dashboard route
      if (currentPath === "/") {
        if (isAuditor) {
          navigate("/auditor/audits");
        } else if (isAdmin) {
          navigate("/admin/dashboard");
        } else {
          navigate("/employee/dashboard");
        }
      }
      setInitialRedirectDone(true);
    }
  }, [
    data,
    loading,
    error,
    frameworkError,
    frameworkLoading,
    navigate,
    location.pathname,
    initialRedirectDone,
    isRestrictedAccess,
  ]);

  // Update employee/auditor mode when pathname changes (after initial load)
  useEffect(() => {
    if (isRestrictedAccess) return;
    if (initialRedirectDone && data?.getAccountDetails?.userDetails) {
      const currentPath = location.pathname;
      const userDetails = data.getAccountDetails.userDetails || {};
      const isAuditor = userDetails?.roles?.some(
        (role) => role?.role_description == Role.AUDITOR
      );

      // Redirect auditors away from admin routes
      if (isAuditor && currentPath.startsWith("/admin/")) {
        navigate("/auditor/audits", { replace: true });
        return;
      }

      // Hardcoded employee route detection
      const isEmployeeRoute =
        currentPath.startsWith("/employee/") ||
        currentPath.startsWith("/policy/ack/") ||
        currentPath.startsWith("/training/");
      const isAuditorRoute = currentPath.startsWith("/auditor/");

      if (isEmployeeRoute) {
        setIsEmployeeMode(true);
        setIsAuditorMode(false);
      } else if (isAuditorRoute) {
        setIsEmployeeMode(false);
        setIsAuditorMode(true);
      } else if (currentPath.startsWith("/admin/")) {
        setIsEmployeeMode(false);
        setIsAuditorMode(isAuditor);  // Keep auditor mode if user is auditor
      }
    }
  }, [location.pathname, initialRedirectDone, data, navigate, isRestrictedAccess]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);

    // Define the route based on the tab clicked
    let route = `/${tab?.replace(/\s+/g, "-").toLowerCase()}`;

    // Check if the tab has a predefined route; otherwise, fallback to a dynamic route
    switch (tab) {
      case "Chat":
        route = "/chat";
        break;
      case "Dashboard":
        route = isEmployeeMode ? "/employee/dashboard" : "/admin/dashboard";
        break;
      case "Controls":
        route = "/admin/controls";
        break;
      case "Integrated Evidences":
        route = "/admin/integrated-evidences";
        break;
      case "Evidence":
        route = "/admin/evidence";
        break;
      case "Policies":
        route = isEmployeeMode ? "/employee/policies" : "/admin/policies";
        break;
      case "Tasks":
        route = "/admin/tasks";
        break;
      case "Framework":
        route = "/framework";
        break;
      case "Policy Enforcement":
        route = "/policy-enforsement";
        break;
      case "Documents":
        route = "/documents";
        break;
      case "Risk Management":
        route = "/admin/risks";
        break;
      case "Access Management":
        route = "/admin/access";
        break;
      case "Vendors":
        route = "/admin/vendors";
        break;
      case "Vulnerabilities":
        route = "/vulnerabilities";
        break;
      case "People":
        route = "/people";
        break;
      case "Devices":
        route = "/devices";
        break;
      case "Infrastructure":
        route = "/infrastructure";
        break;
      case "Code Security":
        route = "/admin/code-security";
        break;
      case "Audit Logs":
        route = "/audit-logs";
        break;
      case "Audits":
        route = isAuditorMode ? "/auditor/audits" : "/admin/audits";
        break;
      case "Integrations":
        route = "/integrations";
        break;
      case "Frameworks":
        route = "/frameworks";
        break;
      case "Admins":
        route = "/admins";
        break;
      case "Policies & Docs":
        route = "/policies-n-docs";
        break;
      case "Company Settings":
        route = "/company-settings";
        break;
      case "Training":
        // route = "/employee/training";
        route = isEmployeeMode ? "/employee/training" : "/admin/training";
        break;
      case "Assets":
        route = isEmployeeMode ? "/employee/assets" : "/admin/assets";
        break;
      default:
        // Keep the tab name as route if no predefined route exists
        route = `/${tab?.replace(/\s+/g, "-").toLowerCase()}`;
    }

    // Navigate to the selected route
    navigate(route);
  };

  const handleSwitchRole = () => {
    if (isEmployeeMode) {
      navigate("/employee/dashboard");
    } else {
      navigate("/admin/dashboard");
    }
    setIsEmployeeMode(!isEmployeeMode);
    setActiveTab("Dashboard");
  };

  const openSecurityDetails = (data, origin, index) => {
    const formattedTitle = data.frameworkName
      ?.replace(/\s+/g, "-")
      .toLowerCase();
    setSecurityDetailsData(data);
    setShowSecurityDetails(true);
    navigate(`/frameworks/details/${formattedTitle}?id=${index}`);
  };

  const closeSecurityDetails = () => {
    setShowSecurityDetails(false);
    setSecurityDetailsData(null);
  };

  // Render error message if data is null or API error occurs
  if (errorMessage) {
    return <UnauthorizePage message={errorMessage} />;
  }

  const isAdminUser = data?.getAccountDetails?.userDetails?.roles?.some(
    (role) => role?.role_description === Role.ADMIN
  );
  if (isRestrictedAccess && data && !isAdminUser) {
    return <UnauthorizePage />;
  }

  return (
    <div style={{ display: "flex", overflowX: "hidden" }}>
      {/* Sidebar */}
      <Sidebar
        drawerOpen={drawerOpen}
        handleDrawerToggle={handleDrawerToggle}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        isEmployeeMode={isEmployeeMode}
        isAuditorMode={isAuditorMode}
        closeSecurityDetails={closeSecurityDetails}
        navigate={navigate}
        drawerItem={data?.getAccountDetails?.userDetails?.permissions}
      />

      {/* Right Content Area */}
      <MainContent
        drawerOpen={drawerOpen}
        sx={{
          position: "relative",
          height: "100vh",
          overflow: "auto",
          backgroundColor: "#F9FAFB",
        }}
      >
        <Navbar
          isEmployeeMode={isEmployeeMode}
          activeTab={activeTab}
          BreadcrumbLists={breadcrumbLists}
          showSecurityDetails={showSecurityDetails}
          securityDetailsData={securityDetailsData}
          onBack={closeSecurityDetails}
          handleSwitchRole={handleSwitchRole}
          dashboardData={data}
        />
        <Box sx={{ padding: "20px 44px" }}>
          <AppRoutes
            openSecurityDetails={openSecurityDetails}
            securityDetailsData={securityDetailsData}
            closeSecurityDetails={closeSecurityDetails}
            setBreadcrumbLists={setBreadcrumbLists}
            isEmployeeMode={isEmployeeMode}
            isAuditorMode={isAuditorMode}
            dashboardData={data?.getAccountDetails}
          />
        </Box>
      </MainContent>
    </div>
  );
}

export default Mainpage;
